import { NotifyHelper } from 'classes/helpers/notify.helper';
import env from 'config';
import { t } from 'i18next';
import { ISnapshotCookie } from 'interfaces/cookies/i-snapshot.cookie';
import { IFireStateDates } from 'interfaces/i-firing';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { QueueState } from 'lib_ts/enums/machine-msg.enum';
import { MachineStatus } from 'lib_ts/enums/machine.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IMachineState } from 'lib_ts/interfaces/i-machine-state';
import {
  IMSSnapshotData,
  ISessionEvent,
} from 'lib_ts/interfaces/i-session-event';
import { IMachineHeartbeatMsg } from 'lib_ts/interfaces/machine-msg/i-machine-heartbeat';
import { IReadyMsg } from 'lib_ts/interfaces/machine-msg/i-ready';
import { isEqual, isNil } from 'lodash';
import { SessionEventsService } from 'services/session-events.service';
import { create } from 'zustand';

type NotifyBoolean = boolean | 'notify';

type R2fStateSlice = {
  noBall: boolean;
  r2fData: IReadyMsg | undefined; // for intercom snapshot, but also empty carousel and eject balls
  // todo: refs of this could just point to r2fData.status
  // the (overall) R2F status from the latest msg
  r2fReady: boolean;
  // the last time any R2F msg was received
  r2fDate: Date | undefined; // * IMPORTANT: update this whenever setting r2fReady
  // ? purpose: block sending to machine when in a bad state (e.g. e-stop recovery or error)
  // whether the machine is ready to receive a mstarget, based on the most recent r2f machine status
  s2mReady: boolean;

  // ! deprecate this if machine won't send heartbeat
  heartbeatData: IMachineHeartbeatMsg | undefined;

  fireStateDates: IFireStateDates;
};

type R2fActionSlice = {
  readonly setR2fData: (data: IReadyMsg | undefined) => void;
  readonly setS2mReady: (status: MachineStatus) => void;
};

type QueueStateSlice = {
  queueState: QueueState;
  // i.e. the user name, not the user ID
  activeUser: Nullable<string> | undefined;
  queueWsIDs: string[];
};

type QueueActionsSlice = {
  // only set if the value is different from the current value
  readonly lazySetQueueWsIDs: (v: string[]) => void;
};

type HealthStateSlice = {
  calibrated: boolean;

  // when force notification is received while not calibrated and can calibrate
  canCalibrate: NotifyBoolean;

  // when force notification is received while e-stop is pressed
  eStopPressed: NotifyBoolean;
};

type IntercomStateSlice = {
  // currently just for reference, nothing needs to use this
  intercomUrl: string | undefined;
  intercomHash: string | undefined;
};

type IntercomActionsSlice = {
  // null => when a new URL is not necessary and nothing was changed
  // undefined => error
  // string => successfully created a new snapshot event and URL is updated
  readonly generateIntercomUrl: (config: {
    pitchID: string | undefined;
    lastMS: IMachineState | undefined;
    snapshot: ISnapshotCookie;
  }) => Promise<Nullable<string> | undefined>;
};

type State = R2fStateSlice &
  QueueStateSlice &
  HealthStateSlice &
  IntercomStateSlice;

type Actions = R2fActionSlice & QueueActionsSlice & IntercomActionsSlice;

type Store = State & Actions;

const isS2mReady = (machine_status: MachineStatus) =>
  [MachineStatus.IDLE, MachineStatus.R2F, MachineStatus.LOADING].includes(
    machine_status
  );

const useMachineStatusStore = create<Store>((set, get) => ({
  noBall: false,
  r2fDate: undefined,
  r2fReady: false,
  r2fData: undefined,
  s2mReady: false,

  setR2fData: (data) => {
    if (!data) {
      set({
        noBall: false,
        r2fData: undefined,
        r2fReady: false,
        r2fDate: undefined,
        s2mReady: false,
      });
      return;
    }

    set({
      noBall: data.machine_status === MachineStatus.NO_BALL,
      r2fData: data,
      r2fReady: data.status,
      r2fDate: new Date(),
      s2mReady: isS2mReady(data.machine_status ?? MachineStatus.UNKNOWN),
    });
  },

  setS2mReady: (status) => {
    set({
      s2mReady: isS2mReady(status),
    });
  },

  heartbeatData: undefined,

  fireStateDates: {
    lastFireDate: null,
    awaitTrainingUntil: new Date(),
    awaitFiringUntil: new Date(),
    ignoreR2fUntil: new Date(),
    lastR2fDate: new Date(),
    loadingUntil: new Date(),
  },

  queueState: QueueState.MachineDC,
  activeUser: undefined,
  queueWsIDs: [],
  lazySetQueueWsIDs: (v) => {
    const { queueWsIDs } = get();
    if (isEqual(queueWsIDs, v)) {
      return;
    }

    set({ queueWsIDs: v });
  },

  calibrated: false,
  canCalibrate: false,
  eStopPressed: false,

  intercomHash: undefined,
  intercomUrl: undefined,
  generateIntercomUrl: async (config) => {
    try {
      const { intercomHash, r2fData } = get();

      const data: IMSSnapshotData = {
        pitch_id: config.pitchID,
        r2f: r2fData ?? null,
        last_ms: config.lastMS ?? null,
      };

      const hash = MiscHelper.hashify(data);

      if (isNil(hash) || hash === intercomHash) {
        // avoid recreating snapshots of basically identical payloads (excluding past_snapshot_ids)
        return null;
      }

      // keep track of any previous snapshots for the session
      data.past_snapshot_ids = config.snapshot.past_snapshot_ids;

      const result = await SessionEventsService.createOne({
        category: 'machine',
        trigger: 'intercom',
        tags: 'snapshot',
        data: data,
      });

      if (!result.success) {
        throw new Error('Failed to create machine snapshot.');
      }

      const newEvent = result.data as ISessionEvent;
      const nextUrl = `${env.server_url}/main/machine-snapshot/${newEvent._id}/html?errors=10`;

      set({
        intercomHash: hash,
        intercomUrl: nextUrl,
      });

      NotifyHelper.debug({
        message_md: 'Machine snapshot created!',
        buttons: [
          {
            label: 'Open URL',
            dismissAfterClick: true,
            onClick: () => window.open(nextUrl),
          },
        ],
      });

      return nextUrl;
    } catch (e) {
      console.error(e);

      NotifyHelper.error({
        message_md: t('common.request-failed-msg'),
      });

      return undefined;
    }
  },
}));

export default useMachineStatusStore;
